import React, { useMemo, useState } from "react";
import moment from "moment";
import Select, { components } from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../pages/revenue.css";

// Shared by the Revenue and Transactions pages: period + client + location filters.
// Every date is a YYYY-MM-DD day in the admin's own timezone, and /revenueReport
// buckets by that timezone too.

export const DATE_FORMAT = "YYYY-MM-DD";
export const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const PRESET_LABELS = {
    today: "Today",
    "7days": "7 days",
    "30days": "30 days",
    month: "This month",
    year: "This year",
    allTime: "All time",
    custom: "Custom",
};

// Which presets each page offers, in display order.
export const REVENUE_PRESETS = ["today", "7days", "30days", "year", "custom"];
export const TRANSACTION_PRESETS = ["today", "7days", "30days", "month", "year", "allTime", "custom"];

// "Beginning of time" floor, far older than any SpotSaver transaction.
const ALL_TIME_FROM = "2000-01-01";

export const datePresetRange = (preset) => {
    const today = moment().format(DATE_FORMAT);
    if (preset === "7days") return { from: moment().subtract(6, "days").format(DATE_FORMAT), to: today };
    if (preset === "30days") return { from: moment().subtract(29, "days").format(DATE_FORMAT), to: today };
    if (preset === "month") return { from: moment().startOf("month").format(DATE_FORMAT), to: today };
    if (preset === "year") return { from: moment().startOf("year").format(DATE_FORMAT), to: today };
    if (preset === "allTime") return { from: ALL_TIME_FROM, to: today };
    return { from: today, to: today };
};

export const money = (v) => {
    const n = Number(v) || 0;
    const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Sign before the symbol ("-$0.31", not "$-0.31"); rounding to $0.00 drops the sign.
    return `${n <= -0.005 ? "-" : ""}$${abs}`;
};

/** "Sep 23, 2026", "Sep 17 – Sep 23, 2026" or "Dec 28, 2025 – Jan 3, 2026". */
export const rangeLabel = (from, to) => {
    if (!from || !to) return "";
    if (from === ALL_TIME_FROM) return "All time";
    const f = moment(from, DATE_FORMAT);
    const t = moment(to, DATE_FORMAT);
    if (from === to) return f.format("MMM D, YYYY");
    if (f.year() === t.year()) return `${f.format("MMM D")} – ${t.format("MMM D, YYYY")}`;
    return `${f.format("MMM D, YYYY")} – ${t.format("MMM D, YYYY")}`;
};

// Maps the `options` block of /revenueReport onto react-select options.
export const toFilterOptions = (options) => ({
    clients: (options?.clients || []).map((c) => ({
        value: c.clientid,
        label: c.status === "ARCHIVE" ? `${c.name} (archived)` : c.name,
    })),
    locations: (options?.locations || []).map((l) => ({
        value: l.storeid,
        label: l.name,
        clientid: l.clientid,
        clientName: l.clientName,
    })),
});

/** Filter state for both pages; `initialPreset` decides the starting range. */
export const useRevenueFilters = (initialPreset = "today") => {
    const initialRange = datePresetRange(initialPreset);
    const [datePreset, setDatePreset] = useState(initialPreset);
    const [dateFrom, setDateFrom] = useState(initialRange.from);
    const [dateTo, setDateTo] = useState(initialRange.to);
    const [client, setClient] = useState(null);
    const [location, setLocation] = useState(null);

    const applyDatePreset = (preset) => {
        setDatePreset(preset);
        if (preset === "custom") return; // keep whatever range was last picked
        const { from, to } = datePresetRange(preset);
        setDateFrom(from);
        setDateTo(to);
    };

    const changeClient = (option) => {
        setClient(option);
        // Drop a location that belongs to a different client.
        if (option && location && location.clientid !== option.value) setLocation(null);
    };

    const reset = () => {
        applyDatePreset(initialPreset);
        setClient(null);
        setLocation(null);
    };

    return {
        datePreset, dateFrom, dateTo, client, location,
        applyDatePreset, setDateFrom, setDateTo, changeClient, setLocation, reset,
    };
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        paddingLeft: 12,
        borderRadius: 8,
        borderColor: state.isFocused ? "#4a32d8" : "#dcdae6",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(74, 50, 216, 0.12)" : "none",
        "&:hover": { borderColor: state.isFocused ? "#4a32d8" : "#c6c3d6" },
        cursor: "pointer",
    }),
    valueContainer: (base) => ({ ...base, paddingLeft: 6, fontSize: 13 }),
    placeholder: (base) => ({ ...base, color: "#19172e", fontWeight: 500 }),
    singleValue: (base) => ({ ...base, color: "#19172e", fontWeight: 500 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, color: "#6b6a80", paddingRight: 10, paddingTop: 6, paddingBottom: 6 }),
    menu: (base) => ({ ...base, zIndex: 20, borderRadius: 8, fontSize: 13, overflow: "hidden" }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "#4a32d8" : state.isFocused ? "#f1effc" : "#fff",
        color: state.isSelected ? "#fff" : "#19172e",
    }),
};

// Leads the select with an icon and a muted name: "[icon] Client  All clients".
const PrefixedControl = ({ children, ...props }) => (
    <components.Control {...props}>
        <span className="material-symbols-outlined revenue-select-icon" aria-hidden="true">{props.selectProps.icon}</span>
        <span className="revenue-select-prefix">{props.selectProps.prefix}</span>
        {children}
    </components.Control>
);

/** Page title row with the Reset and (optional) Export CSV buttons. */
export const RevenuePageHeader = ({ title, subtitle, onReset, onExport, exportDisabled }) => (
    <div className="revenue-page-header">
        <div>
            <h1 className="revenue-page-title">{title}</h1>
            {subtitle && <p className="revenue-page-subtitle">{subtitle}</p>}
        </div>
        <div className="revenue-page-actions">
            {onReset && (
                <button type="button" className="btn revenue-btn-reset" onClick={onReset}>
                    <span className="material-symbols-outlined" aria-hidden="true">sync</span>Reset
                </button>
            )}
            {onExport && (
                <button type="button" className="btn revenue-btn-export" onClick={onExport} disabled={exportDisabled}>
                    <span className="material-symbols-outlined" aria-hidden="true">download</span>Export CSV
                </button>
            )}
        </div>
    </div>
);

const RevenueFilters = ({ filters, clientOptions, locationOptions, presets = REVENUE_PRESETS }) => {
    const {
        datePreset, dateFrom, dateTo, client, location,
        applyDatePreset, setDateFrom, setDateTo, changeClient, setLocation,
    } = filters;

    // Only the picked client's locations; with no client, show the owner next to each.
    const visibleLocationOptions = useMemo(() => {
        const list = client ? locationOptions.filter((l) => l.clientid === client.value) : locationOptions;
        return list.map((l) => ({ ...l, label: !client && l.clientName ? `${l.label} — ${l.clientName}` : l.label }));
    }, [locationOptions, client]);

    const toDate = (iso) => (iso ? moment(iso, DATE_FORMAT).toDate() : null);

    return (
        <div className="revenue-toolbar">
            <div className="revenue-presets" role="group" aria-label="Period">
                {presets.map((p) => (
                    <button
                        key={p}
                        type="button"
                        className={`revenue-preset${datePreset === p ? " active" : ""}`}
                        aria-pressed={datePreset === p}
                        onClick={() => applyDatePreset(p)}
                    >
                        {p === "custom" && <span className="material-symbols-outlined" aria-hidden="true">calendar_today</span>}
                        {PRESET_LABELS[p]}
                    </button>
                ))}
            </div>

            <span className="revenue-toolbar-divider" aria-hidden="true" />

            <div className="revenue-filter">
                <Select
                    value={client}
                    onChange={changeClient}
                    options={clientOptions}
                    placeholder="All clients"
                    isClearable
                    styles={selectStyles}
                    components={{ Control: PrefixedControl }}
                    icon="person"
                    prefix="Client"
                    aria-label="Client"
                />
            </div>
            <div className="revenue-filter">
                <Select
                    value={location}
                    onChange={setLocation}
                    options={visibleLocationOptions}
                    placeholder="All locations"
                    isClearable
                    styles={selectStyles}
                    components={{ Control: PrefixedControl }}
                    icon="location_on"
                    prefix="Location"
                    aria-label="Location"
                />
            </div>

            <div className="revenue-range">
                {datePreset === "custom" ? (
                    <DatePicker
                        selectsRange
                        startDate={toDate(dateFrom)}
                        endDate={toDate(dateTo)}
                        onChange={([start, end]) => {
                            setDateFrom(start ? moment(start).format(DATE_FORMAT) : "");
                            setDateTo(end ? moment(end).format(DATE_FORMAT) : "");
                        }}
                        maxDate={new Date()}
                        placeholderText="Pick a date range"
                        className="revenue-range-input"
                        dateFormat="MMM d, yyyy"
                        popperPlacement="bottom-end"
                    />
                ) : (
                    <span className="revenue-range-label">{rangeLabel(dateFrom, dateTo)}</span>
                )}
            </div>
        </div>
    );
};

export default RevenueFilters;
